import AdapterLuxon from '@mui/lab/AdapterLuxon';
import {
  Checkbox, Divider, MenuItem, TextField,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import { DateTime } from 'luxon';
import { FilterPopover } from 'components/FilterPopover';
import { isNil } from 'lodash';
import { updateRouterQuery } from 'lib/utils/filterUtils';
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { TimeframeOption } from 'components/Filters/utils';

interface Props {
  minPostedAt?: string;
  maxPostedAt?: string;
  options: TimeframeOption[];
  disabled?: boolean;
  filterTitle?: string;
}

export const TimeframeOptionsFilter = ({
  minPostedAt,
  maxPostedAt,
  options = [],
  disabled = false,
  filterTitle = 'Posted date',
}: Props) => {
  const router = useRouter();
  const [selected, setSelected] = useState<string>();

  const [_minPostedAt, setMinPostedAt] = useState<DateTime | null>(
    () => (minPostedAt ? DateTime.fromISO(minPostedAt) : null),
  );
  const [_maxPostedAt, setMaxPostedAt] = useState<DateTime | null>(
    () => (maxPostedAt ? DateTime.fromISO(maxPostedAt) : null),
  );

  // Sync local state with prop changes (when router query changes)
  useEffect(() => {
    setMinPostedAt(minPostedAt ? DateTime.fromISO(minPostedAt) : null);
  }, [minPostedAt]);

  useEffect(() => {
    setMaxPostedAt(maxPostedAt ? DateTime.fromISO(maxPostedAt) : null);
  }, [maxPostedAt]);

  const title = useMemo<string>(() => {
    const optionLabel = options.find((i) => i.label === selected)?.label;

    if (optionLabel) {
      return optionLabel;
    }

    if (_minPostedAt && _maxPostedAt) {
      return `From ${_minPostedAt.toLocaleString()} to ${_maxPostedAt.toLocaleString()}`;
    }

    if (_minPostedAt) {
      return `After ${_minPostedAt.toLocaleString()}`;
    }

    if (_maxPostedAt) {
      return `Before ${_maxPostedAt.toLocaleString()}`;
    }

    return 'Time Frame';
  }, [_maxPostedAt, _minPostedAt, options, selected]);

  // When the component mounts, if there is a minPostedAt but no maxPostedAt,
  // set the minPostedAt in the query. This is used to set the default value.
  useEffect(() => {
    if (_minPostedAt && !_maxPostedAt) {
      if (!router.query.minPostedAt && !router.query.maxPostedAt) {
        updateRouterQuery(router, { minPostedAt: [_minPostedAt.toSQLDate()] });
      }
    }
  }, [_minPostedAt, _maxPostedAt, router]);

  useEffect(() => {
    if (_minPostedAt) {
      // Check for relative options
      const relativeOption = options.find(
        (i) => i.type === 'relative'
          && Math.floor(-1 * _minPostedAt.diffNow('days').days) === i.days,
      );

      if (relativeOption) {
        setSelected(relativeOption.label);
        return;
      }

      // Check for absolute options
      if (_maxPostedAt) {
        const absoluteOption = options.find(
          (i) => i.type === 'absolute'
            && i.minDate === _minPostedAt.toSQLDate()
            && i.maxDate === _maxPostedAt.toSQLDate(),
        );

        if (absoluteOption) {
          setSelected(absoluteOption.label);
          return;
        }
      }
    }

    if (_minPostedAt) {
      setSelected(undefined);
    }
  }, [_minPostedAt, _maxPostedAt, options]);

  const onCustomApply = () => {
    const sMinPostedAt = _minPostedAt?.toSQLDate();
    const sMaxPostedAt = _maxPostedAt?.toSQLDate();
    const changes = {
      ...(sMinPostedAt !== minPostedAt && { minPostedAt: sMinPostedAt || [] }),
      ...(sMaxPostedAt !== maxPostedAt && { maxPostedAt: sMaxPostedAt || [] }),
    };

    if (Object.keys(changes).length > 0) {
      updateRouterQuery(router, changes);
    }
  };

  const onClear = () => {
    setMinPostedAt(null);
    setMaxPostedAt(null);
    setSelected(undefined);

    updateRouterQuery(router, {
      minPostedAt: [],
      maxPostedAt: [],
    });
  };

  const onMinPostedAtChange = (value: DateTime | null) => {
    setMinPostedAt(value);
    setSelected(undefined);
  };

  const onMaxPostedAtChange = (value: DateTime | null) => {
    setMaxPostedAt(value);
    setSelected(undefined);
  };

  const onOptionClick = (option: TimeframeOption) => {
    if (option.type === 'relative') {
      const nextMinPostedAt = DateTime.now().minus({ days: option.days });
      setMinPostedAt(nextMinPostedAt);
      setMaxPostedAt(null);
    } else {
      // absolute
      setMinPostedAt(DateTime.fromISO(option.minDate));
      setMaxPostedAt(DateTime.fromISO(option.maxDate));
    }

    setSelected(option.label);
  };

  return (
    <FilterPopover
      clearable={!isNil(_minPostedAt) || !isNil(_maxPostedAt)}
      onApply={onCustomApply}
      onClear={onClear}
      title={title}
      disabled={disabled}
    >
      <h6 className="font-semibold mb-2">{filterTitle}</h6>

      <div className="-mx-4">
        {options.filter((option) => option.type === 'relative').map((option) => (
          <MenuItem
            key={option.label}
            value={option.label}
            onClick={() => onOptionClick(option)}
            selected={option.label === selected}
          >
            <Checkbox size="small" checked={option.label === selected} />
            {option.label}
          </MenuItem>
        ))}

        {options.some((option) => option.type === 'relative')
          && options.some((option) => option.type === 'absolute') && (
          <Divider className="my-2" />
        )}

        {options.filter((option) => option.type === 'absolute').map((option) => (
          <MenuItem
            key={option.label}
            value={option.label}
            onClick={() => onOptionClick(option)}
            selected={option.label === selected}
          >
            <Checkbox size="small" checked={option.label === selected} />
            {option.label}
          </MenuItem>
        ))}
      </div>

      <Divider className="my-2" />

      Custom Date Range

      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <div className="flex gap-4 my-2">
          <div className="md:w-1/2">
            <DatePicker
              label="From"
              onChange={onMinPostedAtChange}
              renderInput={(inputProps) => <TextField {...inputProps} size="small" />}
              value={_minPostedAt}
            />
          </div>
          <div className="md:w-1/2">
            <DatePicker
              label="To"
              onChange={onMaxPostedAtChange}
              renderInput={(inputProps) => <TextField {...inputProps} size="small" />}
              value={_maxPostedAt}
            />
          </div>
        </div>
      </LocalizationProvider>
    </FilterPopover>
  );
};
